import React, { useRef } from 'react';
import cn from 'classnames';

import Popover, { IPopoverProps, IPopoverRef, PopoverPlacement } from '@creditflowresearch/uilib/Popover';
import IconClose from '@creditflowresearch/uilib/Icon/IconClose';
import UpgradePlanButton from '@/creditflowresearch/ui/common/components/UpgradePlanButton';
import LogInButton from '@/creditflowresearch/ui/common/components/LogInButton/LogInButton';

import styles from './styles.module.scss';
import explanationStyles from '@framework/ui/Atoms/Explanation/Explanation.module.scss';
import IconButton from '@creditflowresearch/uilib/IconButton';

export enum RestrictedButtonType {
  LogIn = 'LogIn',
  UpgradePlan = 'UpgradePlan',
}

interface IProps {
  children?: React.ReactElement,
  title: string,
  description?: string,
  isOpen?: boolean,
  setIsOpen?: (isOpen: boolean) => void,
  triggerEvent?: IPopoverProps['triggerEvent'],
  closeTooltip?: () => void,
  wrapperClassName?: string,
  referenceClassName?: string,
  offsetOptions?: IPopoverProps['offsetOptions'],
  actionButtonType?: RestrictedButtonType,
  customButton?: React.ElementType,
  popoverContentClass?: string,
  placement?: PopoverPlacement,
  usePortal?: boolean,
}

const RestrictedAccessExplanation = ({
  children,
  title,
  description,
  isOpen,
  setIsOpen,
  triggerEvent,
  closeTooltip,
  wrapperClassName,
  referenceClassName,
  offsetOptions,
  actionButtonType = RestrictedButtonType.UpgradePlan,
  customButton: CustomButton,
  popoverContentClass,
  placement,
  usePortal,
}: IProps) => {
  const popoverRef = useRef<IPopoverRef>(null);

  const renderButton = () => {
    if (CustomButton) {
      return <CustomButton />;
    }

    if (actionButtonType === RestrictedButtonType.LogIn) {
      return (
        <LogInButton
          dataTest="restrictedAccessLoginButton"
          className={ styles.logInBtn }
          withIcon={ false }
        />
      );
    }

    return <UpgradePlanButton />;
  };

  const popoverContent = (
    <div className={ styles.popoverContent }>
      <IconButton
        icon={ <IconClose /> }
        color="primary"
        className={ cn(styles.closeBtn, explanationStyles.lightThemeCloseButton) }
        onClick={ closeTooltip }
        dataTest="restrictedAccessTooltipCloseButton"
        ariaLabel="Close"
      />
      <h3 className={ styles.title } data-test="restrictedAccessExplanationTitle">{ title }</h3>
      <div
        className={ styles.description }
        dangerouslySetInnerHTML={ { __html: description } }
        data-test="restrictedAccessExplanationDescription"
      />
      { renderButton() }
    </div>
  );

  return (
    <Popover
      ref={ popoverRef }
      content={ popoverContent }
      className={ styles.popover }
      arrowClassName={ styles.popoverArrow }
      contentClassName={ cn(styles.popoverContainer, popoverContentClass) }
      wrapperClassName={ cn(styles.wrapper, wrapperClassName) }
      referenceClassName={ referenceClassName }
      renderOnlyVisible
      isVisible={ isOpen }
      setIsVisible={ setIsOpen }
      triggerEvent={ triggerEvent }
      dataTest="restrictedAccessExplanation"
      afterClose={ closeTooltip }
      offsetOptions={ offsetOptions }
      placement={ placement }
      usePortal={ usePortal }
    >
      { children }
    </Popover>
  );
};

export default RestrictedAccessExplanation;
